/**
 * Display day index for dailyMetrics snapshots.
 * Mirrors app-backend day7MetricSelection (post Sept 14 2026 cutover).
 */

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const CHILE_OFFSET_MS = 3 * 60 * 60 * 1000;

/** Chile start-of-day 2026-09-14 (fixed UTC−3). */
export const DAY7_ELAPSED_CUTOVER = new Date(Date.UTC(2026, 8, 14, 3, 0, 0, 0));

export function isDay7ElapsedCutover(
  publishedAt: Date | string | undefined | null,
): boolean {
  if (!publishedAt) return false;
  return new Date(publishedAt).getTime() >= DAY7_ELAPSED_CUTOVER.getTime();
}

export function isEodCollectedAtLabel(collectedAt: Date): boolean {
  return (
    collectedAt.getUTCMilliseconds() === 999 &&
    collectedAt.getUTCSeconds() === 59 &&
    collectedAt.getUTCMinutes() === 59 &&
    collectedAt.getUTCHours() === 2
  );
}

/** EOD …T02:59:59.999Z → 02:00 Chile that calendar day; else as-is. */
export function effectiveCollectAt(collectedAt: Date | string): Date {
  const at = new Date(collectedAt);
  if (!isEodCollectedAtLabel(at)) return at;

  const chilean = new Date(at.getTime() - CHILE_OFFSET_MS);
  return new Date(
    Date.UTC(
      chilean.getUTCFullYear(),
      chilean.getUTCMonth(),
      chilean.getUTCDate(),
      5,
      0,
      0,
      0,
    ),
  );
}

export function getElapsedDaysSincePublished(
  publishedAt: Date | string,
  collectedAt: Date | string,
): number {
  const elapsedMs =
    effectiveCollectAt(collectedAt).getTime() - new Date(publishedAt).getTime();
  if (elapsedMs < 0) return 0;
  return Math.floor(elapsedMs / MS_PER_DAY);
}

/**
 * Day N label for UI chips / chart axes.
 * Post-cutover: floor(effective elapsed / 1d) → Day 0 if &lt;24h.
 * Pre-cutover: stored daysSincePublished, else Chilean calendar diff.
 */
export function getDisplayDaySincePublished(
  publishedAt: Date | string | undefined | null,
  collectedAt: Date | string,
  storedDaysSincePublished?: number | null,
): number {
  if (!publishedAt) {
    return storedDaysSincePublished ?? 0;
  }

  if (isDay7ElapsedCutover(publishedAt)) {
    return getElapsedDaysSincePublished(publishedAt, collectedAt);
  }

  if (
    storedDaysSincePublished != null &&
    Number.isFinite(storedDaysSincePublished)
  ) {
    return storedDaysSincePublished;
  }

  const pub = new Date(publishedAt);
  const col = new Date(collectedAt);
  const pubChile = new Date(pub.getTime() - CHILE_OFFSET_MS);
  const colChile = new Date(col.getTime() - CHILE_OFFSET_MS);
  const pubDay = Date.UTC(
    pubChile.getUTCFullYear(),
    pubChile.getUTCMonth(),
    pubChile.getUTCDate(),
  );
  const colDay = Date.UTC(
    colChile.getUTCFullYear(),
    colChile.getUTCMonth(),
    colChile.getUTCDate(),
  );
  return Math.floor((colDay - pubDay) / MS_PER_DAY);
}
